
import { memo, useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Markup, { bodyClass } from "./EmergencySosMarkup";
import { usePage } from "../hooks/usePage";

import { getBooths } from "../store/slices/booth";

import {
  getSosAlerts,
  resolveSos,
  cancelSos,
} from "../store/slices/sos";

const PageMarkup = memo(Markup);

export default function EmergencySos() {
  const dispatch = useDispatch();

  // ---------------------------------
  // AUTH
  // ---------------------------------

  const auth = useSelector((state) => state.auth);

  const user =
    auth?.user ||
    auth?.admin ||
    auth?.data?.user ||
    null;

  // ---------------------------------
  // PAGE CONFIGURATION
  // ---------------------------------

  usePage({
    title:
      "Emergency SOS & Operations Grid Command - Election Police Live Tracker",
    bodyClass,
  });

  // ---------------------------------
  // BOOTH REDUX STATE
  // ---------------------------------

  const {
    booths = [],
    loading: boothsLoading,
    error: boothsError,
  } = useSelector((state) => state.booth || {});

  // ---------------------------------
  // SOS REDUX STATE
  // ---------------------------------

  const {
    sosAlerts = [],
    loading: sosLoading,
    error: sosError,
  } = useSelector((state) => state.sos || {});

  // Individual action loading IDs
  const [resolvingId, setResolvingId] = useState(null);
  const [cancellingId, setCancellingId] = useState(null);

  // ---------------------------------
  // FETCH SOS ALERTS
  // ---------------------------------

  const fetchSosAlerts = useCallback(() => {
    return dispatch(
      getSosAlerts({
        page: 1,
        limit: 100,
      })
    );
  }, [dispatch]);

  // ---------------------------------
  // LOGOUT
  // ---------------------------------

  const handleLogout = () => {
    const confirmed = window.confirm(
      "Are you sure you want to logout?"
    );

    if (!confirmed) return;

    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("user");
    localStorage.removeItem("admin");

    sessionStorage.clear();

    if (window.adminSocket) {
      window.adminSocket.disconnect();
      window.adminSocket = null;
    }

    window.location.replace("/login");
  };

  // ---------------------------------
  // RESOLVE SOS
  // ---------------------------------

  const handleResolveSos = useCallback(
    async (sosId) => {
      if (!sosId || resolvingId || cancellingId) return;

      const confirmed = window.confirm(
        "Are you sure you want to resolve this SOS alert?"
      );

      if (!confirmed) return;

      try {
        setResolvingId(sosId);

        await dispatch(
          resolveSos({
            id: sosId,
            payload: {},
          })
        ).unwrap();

        window.dispatchEvent(
          new CustomEvent("admin:sos-resolved", {
            detail: { _id: sosId },
          })
        );

        window.alert("SOS alert resolved successfully.");
      } catch (error) {
        console.error("Resolve SOS error:", error);

        window.alert(
          typeof error === "string"
            ? error
            : error?.message ||
            "Unable to resolve SOS alert."
        );
      } finally {
        setResolvingId(null);
      }
    },
    [dispatch, resolvingId, cancellingId]
  );

  // ---------------------------------
  // CANCEL SOS
  // ---------------------------------

  const handleCancelSos = useCallback(
    async (sosId) => {
      if (!sosId || resolvingId || cancellingId) return;

      const confirmed = window.confirm(
        "Are you sure you want to cancel this SOS alert?"
      );

      if (!confirmed) return;

      try {
        setCancellingId(sosId);

        await dispatch(
          cancelSos({
            id: sosId,
            payload: {
              cancellationNote: "SOS cancelled by admin",
            },
          })
        ).unwrap();

        window.dispatchEvent(
          new CustomEvent("admin:sos-cancelled", {
            detail: { _id: sosId },
          })
        );

        window.alert("SOS alert cancelled successfully.");
      } catch (error) {
        console.error("Cancel SOS error:", error);

        window.alert(
          typeof error === "string"
            ? error
            : error?.message ||
            "Unable to cancel SOS alert."
        );
      } finally {
        setCancellingId(null);
      }
    },
    [dispatch, resolvingId, cancellingId]
  );

  // ---------------------------------
  // INITIAL DATA
  // ---------------------------------

  useEffect(() => {
    dispatch(getBooths({ page: 1, limit: 1000 }));
    dispatch(getSosAlerts({ page: 1, limit: 100 }));
  }, [dispatch]);

  // ---------------------------------
  // PASS LIVE BOOTHS TO LEGACY CODE
  // ---------------------------------

  useEffect(() => {
    if (Array.isArray(booths)) {
      window.setEmergencyLiveBooths?.(booths);
    }
  }, [booths]);

  // ---------------------------------
  // SOCKET EVENT REFRESH
  // ---------------------------------

  useEffect(() => {
    const handleSosUpdate = () => {
      fetchSosAlerts();
    };

    window.addEventListener(
      "admin:sos-created",
      handleSosUpdate
    );

    window.addEventListener(
      "admin:sos-resolved",
      handleSosUpdate
    );

    window.addEventListener(
      "admin:sos-cancelled",
      handleSosUpdate
    );

    return () => {
      window.removeEventListener(
        "admin:sos-created",
        handleSosUpdate
      );

      window.removeEventListener(
        "admin:sos-resolved",
        handleSosUpdate
      );

      window.removeEventListener(
        "admin:sos-cancelled",
        handleSosUpdate
      );
    };
  }, [fetchSosAlerts]);

  // ---------------------------------
  // RENDER
  // ---------------------------------

  return (
    <PageMarkup
      booths={Array.isArray(booths) ? booths : []}
      boothsLoading={boothsLoading}
      boothsError={boothsError}

      sosAlerts={Array.isArray(sosAlerts) ? sosAlerts : []}
      sosLoading={sosLoading}
      sosError={sosError || ""}

      onRefreshSos={fetchSosAlerts}
      onResolveSos={handleResolveSos}
      onCancelSos={handleCancelSos}

      resolvingId={resolvingId}
      cancellingId={cancellingId}

      user={user}
      handleLogout={handleLogout}
    />
  );
}
